/**
 * Extraction minimale de la date de prise de vue EXIF — feedback
 * alpha (item 10, albums automatiques par métadonnées).
 *
 * On ne parse QUE ce dont on a besoin : le tag `DateTimeOriginal`
 * (0x9003, « YYYY:MM:DD HH:MM:SS ») dans le sous-IFD Exif d'un JPEG.
 * ~80 lignes maison plutôt qu'une dépendance (§3.7) — les autres
 * formats (PNG, HEIC…) retournent `null` et l'appelant retombe sur
 * `file.lastModified`.
 *
 * Références : JPEG = segments `FFxx` ; APP1 (`FFE1`) contient
 * « Exif\0\0 » puis un TIFF (endianness `II`/`MM`) ; IFD0 pointe le
 * sous-IFD Exif via le tag 0x8769.
 */

const TAG_EXIF_IFD = 0x8769;
const TAG_DATETIME_ORIGINAL = 0x9003;

/** Parse « YYYY:MM:DD HH:MM:SS » (heure locale de l'appareil) → ms. */
function parseExifDate(s: string): number | null {
  const m = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(s);
  if (!m) return null;
  const [, y, mo, d, h, mi, se] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(se));
  const ms = date.getTime();
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

/** Décode la partie base64 d'une data URL en octets (JPEG attendu). */
function dataUrlToBytes(dataUrl: string): Uint8Array | null {
  const comma = dataUrl.indexOf(',');
  if (comma < 0 || !dataUrl.startsWith('data:image/')) return null;
  try {
    const bin = atob(dataUrl.slice(comma + 1));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

/**
 * Date de prise de vue EXIF d'une image en data URL, en ms epoch —
 * `null` si le format n'est pas un JPEG EXIF ou que le tag manque.
 * Ne throw jamais (métadonnées corrompues → null).
 */
export function exifDateFromDataUrl(dataUrl: string): number | null {
  try {
    const b = dataUrlToBytes(dataUrl);
    if (!b || b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return null; // pas un JPEG

    // 1. Trouver le segment APP1 « Exif\0\0 ».
    let off = 2;
    while (off + 4 < b.length) {
      if (b[off] !== 0xff) return null;
      const marker = b[off + 1]!;
      const size = (b[off + 2]! << 8) | b[off + 3]!;
      if (marker === 0xe1) break; // APP1
      if (marker === 0xda) return null; // début des données image : plus d'EXIF
      off += 2 + size;
    }
    if (off + 10 >= b.length) return null;
    const exifStart = off + 4; // après FFE1 + taille
    const magic = String.fromCharCode(...b.slice(exifStart, exifStart + 4));
    if (magic !== 'Exif') return null;

    // 2. TIFF : endianness + IFD0.
    const tiff = exifStart + 6;
    const little = b[tiff] === 0x49; // 'II'
    const u16 = (p: number): number =>
      little ? b[p]! | (b[p + 1]! << 8) : (b[p]! << 8) | b[p + 1]!;
    const u32 = (p: number): number =>
      little
        ? (b[p]! | (b[p + 1]! << 8) | (b[p + 2]! << 16) | (b[p + 3]! << 24)) >>> 0
        : ((b[p]! << 24) | (b[p + 1]! << 16) | (b[p + 2]! << 8) | b[p + 3]!) >>> 0;

    const readIfd = (ifdOff: number, wanted: number): number | null => {
      const count = u16(tiff + ifdOff);
      for (let i = 0; i < count; i += 1) {
        const entry = tiff + ifdOff + 2 + i * 12;
        if (u16(entry) === wanted) return entry;
      }
      return null;
    };

    const ifd0 = u32(tiff + 4);
    const exifPtrEntry = readIfd(ifd0, TAG_EXIF_IFD);
    if (!exifPtrEntry) return null;
    const exifIfd = u32(exifPtrEntry + 8);

    const dateEntry = readIfd(exifIfd, TAG_DATETIME_ORIGINAL);
    if (!dateEntry) return null;
    const valOff = u32(dateEntry + 8); // ASCII 20 octets → toujours déporté
    const raw = String.fromCharCode(...b.slice(tiff + valOff, tiff + valOff + 19));
    return parseExifDate(raw);
  } catch {
    return null;
  }
}
